/**
 * Design tokens — single source of truth for all style values.
 *
 * Rules:
 * - All colours, typography, spacing, radii, shadows must come from here.
 * - Styled components import from this file — never hardcode values inline.
 * - Keep in sync with theme.ts (breakpoints, palette).
 */

// ---------------------------------------------------------------------------
// Colours
// ---------------------------------------------------------------------------

export const colors = {
	// Brand
	brand: '#090304',
	brandHover: 'rgba(9,3,4,0.9)',

	// Text (opacity-based — matches MUI palette.text)
	textPrimary: 'rgba(0,0,0,0.87)',
	textSecondary: 'rgba(0,0,0,0.6)',
	textDisabled: 'rgba(0,0,0,0.38)',
	textDisabledOnDark: 'rgba(255,255,255,0.38)',
	textInverse: '#ffffff',
	textAddress: '#475467', // muted address / subtitle text

	// Surfaces
	surfaceWhite: '#ffffff',
	surfaceMuted: '#fafafa',
	// Frosted-glass surface — compact/scrolled header background
	surfaceMutedFrosted: 'rgba(250,250,250,0.95)',
	// Full-page dark background — error/inactive states that render outside the menu layout
	surfaceDark: '#2d2d2d',
	// Pure-white frosted surface — mobile bottom-sheet header background (sold-out warning, etc.)
	surfaceWhiteFrosted: 'rgba(255,255,255,0.95)',
	// Overlays / borders
	border: 'rgba(0,0,0,0.16)',
	borderSubtle: 'rgba(0,0,0,0.12)',
	borderTransparent: 'transparent', // zero-width border placeholder — preserves layout on focus transitions
	divider: 'rgba(0,0,0,0.12)',
	focusBorder: 'rgba(0,0,0,0.3)',
	overlayHeavy: 'rgba(0,0,0,0.54)',
	overlayMedium: 'rgba(0,0,0,0.08)',
	overlayLight: 'rgba(0,0,0,0.06)',
	overlaySubtle: 'rgba(0,0,0,0.04)',
	overlayWhiteSubtle: 'rgba(255,255,255,0.16)',

	// Icon
	iconActive: 'rgba(0,0,0,0.56)',

	// Semantic
	// error / warning — MUI palette values, used for system-level states (form errors, alerts)
	error: '#D32F2F',
	errorAlt: '#B00020', // Figma design uses this for StoreClosed component
	errorAltLight: 'rgba(176,0,32,0.08)',
	errorBg: 'rgba(211,47,47,0.08)',
	errorBorder: 'rgba(211,47,47,0.32)',
	warning: '#ED6C02',
	// errorFigma / warningFigma — Figma design-system values, used for inline UI labels
	// (SoldOutLabel, LowStockLabel) where the designer intentionally chose a different shade.
	// Do not use these interchangeably with error/warning.
	errorFigma: '#D92D20',
	warningFigma: '#F6A735',
	warningBg: 'rgba(237,108,2,0.12)',
	// Figma-spec yellow background for the below-min order amount warning banner.
	// Derived from warningFigma (#F6A735) at 0.12 opacity — matches the pulse animation colours.
	warningYellowLight: 'rgba(246,167,53,0.12)',
	warningPulseStart: 'rgba(246,167,53,0.18)',
	warningPulseEnd: 'rgba(246,167,53,0.2)',
	// Figma: custom/badgeAlertBackground — "Almost Sold Out" pill chip background
	badgeAlertBg: 'rgba(246,167,53,0.16)',
	success: '#4CAF50',
	successText: '#378424', // Figma Contact Information verified row — distinct from MUI success green
	link: '#1d65f4', // Figma inline hyperlink colour (loyalty ToU, etc.)
	amexBlue: '#1f72cd', // American Express brand blue — used for the Amex card icon chip background
	successLight: 'rgba(46,125,50,0.12)',
	errorLight: 'rgba(211,47,47,0.12)',

	// Overlays
	overlayBackdrop: 'rgba(0,0,0,0.25)',
	// Full-page loading overlay (semi-transparent white, allows background to show through)
	overlayPageLoading: 'rgba(255,255,255,0.72)',
} as const

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

export const typography = {
	// Font family
	fontFamily: '"Nunito Sans", sans-serif',

	// Font sizes
	fontSizeXs: 11,
	fontSizeSm: 12,
	fontSizeMd: 14,
	fontSizeLg: 16,
	fontSizeIconSm: 18,
	fontSizeIconMd: 20,
	fontSizeXl: 20,
	fontSizeXxl: 24,
	fontSizeIcon: 22,
	fontSizeIconLg: 24,

	// Font weights
	fontWeightRegular: 400,
	fontWeightSemiBold: 600,
	fontWeightBold: 700,

	// Line heights
	lineHeightTight: 1,
	lineHeightXs: '14px',
	lineHeightSm: '16px',
	lineHeightMd: '20px',
	lineHeightLg: '24px',
	lineHeightXl: '28px',
	lineHeightXxl: '32px',

	// Letter spacing
	letterSpacingTight: '0.1px',
	letterSpacingNormal: '0.15px',
	letterSpacingNormalLg: '0.18px',
	letterSpacingWide: '0.25px',
	letterSpacingWidest: '0.4px',
} as const

// ---------------------------------------------------------------------------
// Spacing
// ---------------------------------------------------------------------------

export const spacing = {
	xxs: 2,
	xs: 4,
	sm: 8,
	md: 12,
	lg: 16,
	xl: 24,
	xxl: 32,
	// Desktop page-level horizontal padding and column gap (Figma 562:39005)
	xxxl: 48,
	// Checkout left-column content inset — Figma Content node 2087-103487 px-[72px]
	xxxxl: 72,
	// Page-level bottom padding — gives breathing room below last content section
	pageBottomPadding: 64,
	// Horizontal padding inside badge chips (Figma-specified, no standard step between xs and sm)
	badgePaddingX: 6,
} as const

// ---------------------------------------------------------------------------
// Border radius
// ---------------------------------------------------------------------------

export const radius = {
	none: 0,
	badge: 4,
	card: 8,
	dragPill: 6,
	dropdown: 12,
	searchBar: 20,
	sheet: 16,
	ctaButton: 64,
	pill: 100,
	circle: '50%',
} as const

// ---------------------------------------------------------------------------
// Shadows
// ---------------------------------------------------------------------------

export const shadows = {
	card: '0px 2px 5px rgba(0,0,0,0.03)',
	drawer: '0px 2px 5px 0px rgba(0,0,0,0.04)',
	panel: '0px 5px 10px rgba(0,0,0,0.05)',
	dropdown: '0px 4px 16px rgba(0,0,0,0.12)',
	modal: '0px 1px 3px rgba(0,0,0,0.2), 0px 2px 1px rgba(0,0,0,0.12), 0px 1px 1px rgba(0,0,0,0.14)',
	floatingButton: '0 1px 4px rgba(0,0,0,0.2)',
} as const

// ---------------------------------------------------------------------------
// Transitions
// ---------------------------------------------------------------------------

export const transitions = {
	fast: '0.15s',
	// Spatial animations — used for layout changes (grid-template-rows, padding, gap)
	spatial: '0.28s cubic-bezier(0.32, 0.72, 0, 1)',
	// Opacity animations — used for fade in/out
	opacity: '0.22s ease',
} as const

// ---------------------------------------------------------------------------
// Effects
// ---------------------------------------------------------------------------

export const effects = {
	backdropBlur: 'blur(12px)',
} as const

// ---------------------------------------------------------------------------
// Z-index
// ---------------------------------------------------------------------------

export const zIndex = {
	// Absolute-positioned overlays layered above sibling content (drag pill, close button)
	absoluteOverlay: 1,
	stickyHeader: 10,
	dropdown: 1000,
	// Must remain above dropdown — delivery address autocomplete overlaps the checkout sticky header otherwise.
	checkoutStickyHeader: 1001,
	modal: 1300,
	storeClosedOverlay: 1400,
} as const

// ---------------------------------------------------------------------------
// Skeleton animation opacities — shared across all skeleton components
// ---------------------------------------------------------------------------

export const skeletonOpacity = {
	high: 0.92,
	mid: 0.72,
	reduced: 0.82,
} as const

// ---------------------------------------------------------------------------
// Component sizing
// ---------------------------------------------------------------------------

export const sizing = {
	// Interactive element heights
	// Both mobile and desktop pills are 32px per the current Figma spec.
	// WCAG 2.5.5 recommends 44px touch targets on mobile — this is an intentional
	// design decision; revisit if accessibility audits flag it.
	pillHeightMobile: 32,
	pillHeightDesktop: 32,
	iconButton: 40,
	addButton: 36,
	searchBarHeight: 32,
	searchBarWidth: 160,
	searchClearButton: 20,
	navIcon: 20,
	iconSm: 24,
	iconMd: 56,
	iconSuccess: 64,

	// Avatar
	avatarMobile: 48,
	avatarDesktop: 64,

	// Card dimensions
	cardHeightMobile: 128,
	cardHeightDesktop: 144,
	cardHeightOA: 104,
	cardImageWidth: 144,
	cardImageOAWidth: 90,

	// Layout
	cartPanelHeaderHeight: 72, // Figma checkout cart panel header
	cartSidebarWidth: 320, // lg (1025px) — compact desktop
	cartSidebarWidthNarrow: 313, // lgNarrow range (1025–1132px) — reduced sidebar to give menu column more room
	cartSidebarWidthWide: 400, // xl (1280px+) — wide desktop
	layoutMaxWidth: 1280,
	// Sold-out warning modal — Figma-specified dimensions
	soldOutModalWidth: 400, // desktop centred modal width (Figma node 2908:81336)
	soldOutDrawerWidth: 480, // mobile bottom-sheet max width (Figma node 2908:78613)
	// User Profile success confirmation modal — desktop centred modal width
	userProfileSuccessModalWidth: 400,
	// Max content width at tablet (md) — Figma tablet frame is 1024px wide with content
	// constrained to 832px centred inside a full-width container with 16px side padding.
	tabletContentMaxWidth: 832,

	// Skeleton block heights — match typography line-height scale
	skeletonTextHeightMd: 20,
	skeletonTextHeightSm: 16,
	skeletonHeadingMobile: 24,
	skeletonHeadingDesktop: 28,
	skeletonPriceWidth: 48,
	// Page bottom padding
	pageBottomPadding: 64,

	// Skeleton pill placeholder widths (rem — proportional, not pixel-exact)
	skeletonPillWidths: ['4.5rem', '5.25rem', '3.75rem', '6rem'] as string[],
	// Skeleton category header widths
	skeletonCategoryHeaderWidths: ['7rem', '5.5rem', '8rem'] as string[],
	// Skeleton item title widths (percentage strings)
	skeletonItemTitleWidths: ['72%', '58%', '80%', '50%', '65%', '68%'] as string[],
	// Skeleton item description line widths
	skeletonItemDescWidths: ['82%', '60%'] as string[],

	// Layout min-height — svh excludes mobile browser chrome (avoids iOS overscroll)
	layoutMinHeight: '100svh',

	// Cart item thumbnail
	cartItemImage: 48,

	// Misc
	loaderSize: 16,
	hamburgerBadgeMinWidth: 16,
	moreMenuMinWidth: 160,
	emptySearchMinHeight: 294,
	clearIcon: 14,
	// Offset scroll-to-section so category headings land below the sticky header
	stickyHeaderScrollOffset: 180,
	// Mobile bottom sheet max height
	mobileSheetMaxHeight: '80vh',
	// Sign-in / generic modal bottom sheet max height (Figma: modal drops from top, 85vh on mobile)
	modalSheetMaxHeight: '85vh',
	// Sold-out warning desktop modal max height — prevents overflow on short viewports
	soldOutModalMaxHeight: '90vh',
	// Review Order drawer min height — ensures consistent panel height when cart is empty
	reviewOrderModalMinHeight: 420,
	reviewOrderModalEmptyMinHeight: 460,
	// Compact viewport (≤360px) — reduced to fit narrower screens
	reviewOrderModalMinHeightSm: 340,
	reviewOrderModalEmptyMinHeightSm: 380,
	// Minimum card width — single-column (mobile) and two-column (desktop) layouts
	menuItemMinWidthBase: 280,
	menuItemMinWidth: 260,
	// Add button circle offset from card bottom-right corner
	addButtonOffset: 7,
	// Store Closed modal
	storeClosedModalWidth: 400,
	storeClosedLogoSize: 64,
	// V2 access gate card
	gateCardMaxWidth: 480,
	// Figma specifies a larger offset for Order Again cards (9px vs 7px for regular)
	addButtonOffsetReOrder: 9,

	// Checkout
	checkoutButton: 40, // PAY NOW button height — Figma checkout cart panel
	checkoutScrollIndicator: 40, // Floating scroll-down circle diameter — Figma 2031-88298
	// Express Checkout wallet buttons — Figma-measured minimum width before button degrades visually
	walletButtonMinWidth: 210,
	walletButtonHeight: 50,
	checkoutHeaderRootHeight: 72, // Figma checkout header height (desktop)
	// Coupon section — compact mobile (≤360px) sizing per Figma coupon row spec
	couponInputHeightCompact: 56,
	couponApplyWidthCompact: 77,

	// Tip section button dimensions (Figma tip preset row spec)
	tipPresetButtonHeightMobile: 72,
	tipPresetButtonHeightDesktop: 56,
	tipPresetButtonMinWidth: 100,
	tipCustomInputMaxWidth: 72,

	// Gift card modal dimensions (Figma gift card modal spec)
	giftCardModalMaxWidth: 400,
	giftCardModalHeaderHeight: 72,
	giftCardModalHeaderTopPadding: 36, // Figma gift card modal header — mobile top padding
	giftCardModalContentHeight: 136,
	giftCardAppliedRemoveButtonSize: 16,
	giftCardAppliedNumberGap: 10, // Figma: gap between masked number and inline X button
	iconXs: 10, // Smallest icon — inline remove button in gift card applied row

	// Item modal measurements
	compactHeaderMaxHeight: 120,
	itemMetaMaxHeight: 400,
	itemImageHeight: 240,
	badgeSize: 20, // required badge + low-stock badge height/width
	thumbSize: 44, // compact-header thumbnail
	specialRequestMinHeight: 56,

	// Drag pill handle (Figma-specified: 80×8px, borderRadius 6)
	dragPillWidth: 80,
	dragPillHeight: 8,

	// Close button (absolute-positioned within modal header/hero)
	closeButton: 32,
	// Compact badge height — scroll-triggered item header (smaller than the 20px content-area badge)
	compactBadgeHeight: 16,

	// Modifier set row / action bar
	modifierRowMinHeight: 56,
	modifierBarMinHeight: 72,

	// Header — Figma-derived measurements for the 720px tablet breakpoint
	buttonHeight: 36,
	signInButtonMinWidth: 117,
	schedulePillMaxWidth: 269,
	brandingBoxMaxWidth: 491,
	// Icon-only sign-in button (compact breakpoint)
	signInIconButton: 36,
	// Avatar size in compact/scrolled header
	avatarCompact: 36,

	// SignIn modal
	contentBoxMinHeight: 280, // StyledContentBox min-height
	signInModalHeaderHeight: 73, // StyledHeaderBox desktop height (includes border)
	signInVerifyButtonHeight: 40, // StyledButton verify/submit button height
	otpInputWidth: 54, // OTP box desktop width — 20px content + 2×16px padding + 2px border (Figma)
	otpInputHeight: 58, // OTP box desktop height — 24px content + 2×16px padding + 2px border (Figma)
	mobileHeaderPaddingTop: 36, // StyledHeaderBox mobile top padding
	mobileHeaderPaddingBottom: 16, // StyledHeaderBox mobile bottom padding
	mobileHeaderMinHeight: 69, // 12px (top) + 40px (content) + 16px (bottom) + 1px (border)
	mobileFooterMinHeight: 72, // 16px (padding) + 40px (button) + 16px (padding)

	// SignIn modal phone input
	phoneInputCountryButtonWidth: 118, // 84px inner content + 2×16px padding + 2px border
	mobilePhoneInputHeight: 56, // 8px + 40px + 8px padding
	mobilePhoneInputWidth: 188, // 16px + 1px + 154px + 1px + 16px
	mobileOtpInputWidth: 47.3, // 16px + 13.3px + 16px + 2px
	mobileOtpInputHeight: 58, // 16px + 24px + 16px + 2px
	compactOtpInputWidth: 45, // OTP box width at ≤360px (compact breakpoint)
	xsOtpInputWidth: 30, // OTP box width below 360px (very small devices)
	mobileEmailFieldHeight: 58, // 8px + 40px + 8px + 2px
	mobileEmailFieldWidth: 314, // 16px + 280px + 16px + 2px

	// User profile page — compact view (360px)
	compactInputFieldWidth: 294, // Full-width input field at ≤361px breakpoint

	// Order settings edit modal
	editModalTitleMaxWidth: 211,
	// Order type tiles (mobile <= 720px)
	orderTypeTileHeightMobile: 108,
	orderTypeTileWidthCompactPct: 45, // percentage — both tiles fit side-by-side at ≤360px
} as const

// ---------------------------------------------------------------------------
// Typography presets — named combinations of size + weight + lineHeight + letterSpacing
// Use these instead of repeating the four properties individually.
// ---------------------------------------------------------------------------

export const typePresets = {
	// UI labels, buttons, pill text
	labelMd: {
		fontSize: typography.fontSizeMd,
		fontWeight: typography.fontWeightBold,
		lineHeight: typography.lineHeightMd,
		letterSpacing: typography.letterSpacingWide,
	},
	// Larger labels — sheet items, cancel buttons, section CTAs
	labelLg: {
		fontSize: typography.fontSizeMd,
		fontWeight: typography.fontWeightBold,
		lineHeight: typography.lineHeightLg,
		letterSpacing: typography.letterSpacingWide,
	},
	// Body copy, inputs, descriptions
	bodyMd: {
		fontSize: typography.fontSizeMd,
		fontWeight: typography.fontWeightRegular,
		lineHeight: typography.lineHeightMd,
		letterSpacing: typography.letterSpacingWide,
	},
	// Section headings, sheet titles, category names
	headingLg: {
		fontSize: typography.fontSizeLg,
		fontWeight: typography.fontWeightBold,
		lineHeight: typography.lineHeightLg,
		letterSpacing: typography.letterSpacingNormal,
	},
	// Page/panel headings — 20px Bold (H2 in checkout panels, cart section titles)
	headingXl: {
		fontSize: typography.fontSizeXl,
		fontWeight: typography.fontWeightBold,
		lineHeight: typography.lineHeightXl,
		letterSpacing: typography.letterSpacingNormal,
	},
	// Captions, tags, secondary labels
	bodySm: {
		fontSize: typography.fontSizeSm,
		fontWeight: typography.fontWeightRegular,
		lineHeight: typography.lineHeightSm,
		letterSpacing: typography.letterSpacingWidest,
	},
	// Item modal main heading — Figma: 24px SemiBold, 32px line-height
	headingXxl: {
		fontSize: typography.fontSizeXxl,
		fontWeight: typography.fontWeightSemiBold,
		lineHeight: typography.lineHeightXxl,
		letterSpacing: typography.letterSpacingNormal,
	},
} as const

// ---------------------------------------------------------------------------
// Focus styles — shared across all interactive elements
// ---------------------------------------------------------------------------

export const focusVisible = {
	outline: '2px solid',
	outlineOffset: 2,
} as const

// ---------------------------------------------------------------------------
// Breakpoints — sourced from Figma Online Ordering V2 design file
//   compact       ≤ 360px  (sign-in pill collapses to icon-only)
//   mobileMd   361–479px   (mid-mobile — tip section button direction threshold)
//   mobile        ≤ 720px  (tablet-mobile range)
//   desktopSm   721–1024px
//   desktopLg    ≥ 1025px
// ---------------------------------------------------------------------------

export const breakpoints = {
	compact: 361,
	mobileMd: 480,
	mobile: 720,
	desktopSm: 721,
	desktopLg: 1025,
} as const
